import { ReactNode, useEffect, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { api, auth } from '../api/client';
import { UserMenu } from './UserMenu';

/**
 * Shell for the org workspace. Admins and staff both see the full menu; staff are limited to
 * their own institution's data by the API (server-side scoping), not by hiding pages.
 */
export function OrgLayout({ children }: { children: ReactNode }) {
  const { orgId = '' } = useParams();
  const nav = useNavigate();
  const user = auth.getUser();
  const isAdmin = user?.role === 'Admin';
  const perms = auth.getPermissions();
  const [orgName, setOrgName] = useState<string | null>(isAdmin ? null : perms.organizationName);
  const [orgCode, setOrgCode] = useState<string | null>(null);
  const [academicOpen, setAcademicOpen] = useState(true);
  const [staffOpen, setStaffOpen] = useState(true);
  const [studentOpen, setStudentOpen] = useState(true);
  const [examsOpen, setExamsOpen] = useState(true);
  // Whether the side panel is hidden. Remembered across pages/refresh.
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('examapp.sidebarCollapsed') === '1');
  const toggleSidebar = () => setCollapsed(c => {
    const next = !c;
    localStorage.setItem('examapp.sidebarCollapsed', next ? '1' : '0');
    return next;
  });
  // Count of published exams with ungraded submissions, shown beside "To Evaluate".
  const [toEvaluateCount, setToEvaluateCount] = useState(0);

  useEffect(() => {
    if (user?.role !== 'Admin' && user?.role !== 'Staff') { nav('/exams', { replace: true }); return; }
    if (!isAdmin) { setOrgName(perms.organizationName); return; }
    // Admins resolve the org from the platform list; staff can't (admin-only endpoint).
    api.listOrganizations()
      .then(list => {
        const found = list.find(o => o.id === orgId);
        if (!found) { nav('/platform/organizations', { replace: true }); return; }
        setOrgName(found.name);
        setOrgCode(found.code);
      })
      .catch(() => { /* surfaced by child pages on their own calls */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  // Compute the "To Evaluate" badge: published exams that have submissions still awaiting grading.
  useEffect(() => {
    if (user?.role !== 'Admin' && user?.role !== 'Staff') return;
    api.listExams()
      .then(list => setToEvaluateCount(
        list.filter(e => e.status === 'Published' && e.submissionCount > 0 && !e.evaluationComplete).length))
      .catch(() => { /* non-fatal */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    'nav-link' + (isActive ? ' active' : '');

  return (
    <div className={'org-shell' + (collapsed ? ' sidebar-collapsed' : '')}>
      <aside className="org-sidebar">
        <div className="org-sidebar-head">
          <div className="org-name">{orgName ?? '…'}</div>
          <button type="button" className="sidebar-collapse-btn" onClick={toggleSidebar}
                  aria-label="Hide menu" title="Hide menu">«</button>
        </div>
        {orgCode && <div className="muted" style={{ padding: '0 10px' }}>{orgCode}</div>}

        <button type="button" className="nav-section toggle"
                aria-expanded={academicOpen}
                onClick={() => setAcademicOpen(o => !o)}>
          <span>Academic</span>
          <span className={'caret' + (academicOpen ? ' open' : '')}>▾</span>
        </button>
        {academicOpen && (
          <div className="nav-submenu">
            <NavLink to={`/org/${orgId}/institutions`} className={linkClass}>Institution</NavLink>
            <NavLink to={`/org/${orgId}/departments`} className={linkClass}>Department</NavLink>
            <NavLink to={`/org/${orgId}/programs`} className={linkClass}>Program</NavLink>
            <NavLink to={`/org/${orgId}/class-sections`} className={linkClass}>Class Section</NavLink>
            <NavLink to={`/org/${orgId}/class-section-details`} className={linkClass}>Class Section Details</NavLink>
            <NavLink to={`/org/${orgId}/academic-years`} className={linkClass}>Academic Year</NavLink>
            <NavLink to={`/org/${orgId}/batch-years`} className={linkClass}>Batch Year</NavLink>
            <NavLink to={`/org/${orgId}/batches`} className={linkClass}>Batch</NavLink>
          </div>
        )}

        <button type="button" className="nav-section toggle"
                aria-expanded={staffOpen}
                onClick={() => setStaffOpen(o => !o)}>
          <span>Staff</span>
          <span className={'caret' + (staffOpen ? ' open' : '')}>▾</span>
        </button>
        {staffOpen && (
          <div className="nav-submenu">
            <NavLink to={`/org/${orgId}/staff-roles`} className={linkClass}>Staff Role</NavLink>
            <NavLink to={`/org/${orgId}/staff`} className={linkClass}>Staff</NavLink>
            <NavLink to={`/org/${orgId}/staff-logins`} className={linkClass}>Staff Login</NavLink>
          </div>
        )}

        <button type="button" className="nav-section toggle"
                aria-expanded={studentOpen}
                onClick={() => setStudentOpen(o => !o)}>
          <span>Student</span>
          <span className={'caret' + (studentOpen ? ' open' : '')}>▾</span>
        </button>
        {studentOpen && (
          <div className="nav-submenu">
            <NavLink to={`/org/${orgId}/students`} className={linkClass}>Student</NavLink>
            <NavLink to={`/org/${orgId}/student-logins`} className={linkClass}>Student Login</NavLink>
          </div>
        )}

        <button type="button" className="nav-section toggle"
                aria-expanded={examsOpen}
                onClick={() => setExamsOpen(o => !o)}>
          <span>Exams</span>
          <span className={'caret' + (examsOpen ? ' open' : '')}>▾</span>
        </button>
        {examsOpen && (
          <div className="nav-submenu">
            <NavLink to={`/org/${orgId}/syllabi`} className={linkClass}>AI Question Paper</NavLink>
            <NavLink to={`/org/${orgId}/exam-schedule`} className={linkClass}>Exam Schedule</NavLink>
            <NavLink to={`/org/${orgId}/exams/drafts`} className={linkClass}>Create Exam</NavLink>
            <NavLink to={`/org/${orgId}/exams/published`} className={linkClass}>Published Exams</NavLink>
            <NavLink to={`/org/${orgId}/exams/to-evaluate`} className={linkClass}>
              To Evaluate{toEvaluateCount > 0 ? ` (${toEvaluateCount})` : ''}
            </NavLink>
            <NavLink to={`/org/${orgId}/exams/results`} className={linkClass}>Results</NavLink>
          </div>
        )}

        <div className="back-link">
          {isAdmin && <NavLink to="/platform/organizations" className="nav-link">← Organizations</NavLink>}
          <button className="nav-link" style={{ width: '100%', textAlign: 'left', border: 'none' }}
                  onClick={() => { auth.clear(); nav('/login'); }}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="org-main">
        <header className="topbar">
          <div className="row" style={{ gap: 12 }}>
            {collapsed && (
              <button type="button" className="topbar-btn" onClick={toggleSidebar}
                      aria-label="Show menu" title="Show menu">
                <span aria-hidden>☰</span>
              </button>
            )}
            <div className="brand">{orgName ?? 'Organization'}</div>
          </div>
          <UserMenu />
        </header>
        <div className="container">{children}</div>
      </main>
    </div>
  );
}
